import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import PageHero from "@/components/PageHero";
import { Search, ExternalLink, Star, Sparkles } from "lucide-react";

export default function Bookstore() {
  const { settings } = useSettings();
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [genre, setGenre] = useState("all");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/books?status=published,coming_soon").then((r) => setBooks(r.data));
  }, []);

  const categories = useMemo(() => ["all", ...Array.from(new Set(books.map((b) => b.category).filter(Boolean)))], [books]);
  const genres = useMemo(() => ["all", ...Array.from(new Set(books.map((b) => b.genre).filter(Boolean)))], [books]);

  const filtered = useMemo(() => {
    return books.filter((b) => {
      if (q && !`${b.title} ${b.subtitle} ${b.author_name}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (category !== "all" && b.category !== category) return false;
      if (genre !== "all" && b.genre !== genre) return false;
      if (tab === "featured" && !b.featured) return false;
      if (tab === "new" && !b.new_release) return false;
      if (tab === "bestseller" && !b.bestseller) return false;
      return true;
    });
  }, [books, q, category, genre, tab]);

  return (
    <div className="bg-[color:var(--marupiara-ivory)]">
      <PageHero image={settings.bookstore_hero_image} overline="Livraria" title="Nossa vitrine literária." subtitle="Todos os livros publicados pela Marupiara. Clique em Comprar para adquirir no canal oficial." />

      <section className="py-8 border-b border-[color:var(--marupiara-gold)]/20 bg-[color:var(--marupiara-cream)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--marupiara-text-on-light-muted)]" />
            <input
              data-testid="bookstore-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título ou autor…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-neutral-300 focus:border-[color:var(--marupiara-gold-dark)] focus:outline-none text-sm"
            />
          </div>
          <select data-testid="bookstore-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2.5 bg-white border border-neutral-300 text-sm focus:outline-none focus:border-[color:var(--marupiara-gold-dark)]">
            {categories.map((c) => <option key={c} value={c}>{c === "all" ? "Todas Categorias" : c}</option>)}
          </select>
          <select data-testid="bookstore-filter-genre" value={genre} onChange={(e) => setGenre(e.target.value)} className="px-3 py-2.5 bg-white border border-neutral-300 text-sm focus:outline-none focus:border-[color:var(--marupiara-gold-dark)]">
            {genres.map((c) => <option key={c} value={c}>{c === "all" ? "Todos Gêneros" : c}</option>)}
          </select>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-4 flex flex-wrap gap-2">
          {[
            { k: "all", label: "Todos" },
            { k: "featured", label: "Destaques" },
            { k: "new", label: "Lançamentos" },
            { k: "bestseller", label: "Mais vendidos" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`bookstore-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${tab === t.k ? "bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-gold)]" : "bg-white text-[color:var(--marupiara-text-on-light-muted)] border border-neutral-300 hover:border-[color:var(--marupiara-gold-dark)]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-[color:var(--marupiara-text-on-light-muted)]">Nenhum livro encontrado com esses filtros.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {filtered.map((b) => (
                <div key={b.id} data-testid={`book-card-${b.id}`} className="book-card">
                  <div className="book-card-inner">
                    <Link to={`/livro/${b.slug}`} className="block">
                      <div className="relative aspect-[2/3] overflow-hidden bg-neutral-200 mb-3">
                        {b.cover_url ? (
                          <img src={b.cover_url} alt={b.title} className="book-cover w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-neutral-500 text-xs">Sem capa</div>
                        )}
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          {b.status === "coming_soon" && <span className="bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-gold)] text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 border border-[color:var(--marupiara-gold)]/40"><Sparkles size={10} /> Em Breve</span>}
                          {b.status !== "coming_soon" && b.new_release && <span className="bg-[color:var(--marupiara-gold)] text-[color:var(--marupiara-obsidian)] text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1"><Sparkles size={10} /> Lançamento</span>}
                          {b.status !== "coming_soon" && b.bestseller && <span className="bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-gold)] text-[10px] uppercase tracking-wider px-2 py-0.5 flex items-center gap-1"><Star size={10} /> Bestseller</span>}
                        </div>
                      </div>
                      <div className="overline text-[10px] text-[color:var(--marupiara-gold-dark)]">{b.category}</div>
                      <div className="font-serif text-lg text-[color:var(--marupiara-obsidian)] leading-tight line-clamp-2">{b.title}</div>
                      <div className="text-xs text-[color:var(--marupiara-text-on-light-muted)] mt-0.5">{b.author_name}</div>
                    </Link>
                    {b.status === "coming_soon" ? (
                      <div
                        data-testid={`book-coming-soon-${b.id}`}
                        className="mt-3 w-full flex items-center justify-center gap-1 py-2 border border-[color:var(--marupiara-gold)] bg-[color:var(--marupiara-gold)]/10 text-[color:var(--marupiara-gold-dark)] text-xs uppercase tracking-widest font-semibold"
                      >
                        Em Breve
                      </div>
                    ) : b.buy_link ? (
                      <a
                        href={b.buy_link}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`book-buy-${b.id}`}
                        className="mt-3 w-full flex items-center justify-center gap-1 py-2 border border-[color:var(--marupiara-gold-dark)] text-[color:var(--marupiara-gold-dark)] text-xs uppercase tracking-wider hover:bg-[color:var(--marupiara-gold)] hover:text-[color:var(--marupiara-obsidian)] hover:border-[color:var(--marupiara-gold)] transition-colors"
                      >
                        Comprar <ExternalLink size={12} />
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
